import { Info } from "lucide-react";
import React from "react";

interface OnboardingFormData {
  clientName: string;
  dateSubmitted: string;
  includeEmployeeLifecycle: boolean;

  // Section 1
  pilotCompaniesConfirmed: boolean;
  numberOfPilotCompanies: string;
  childOrganizations: { name: string; domain: string; contactEmail: string }[];

  // Section 2
  legalCompanyName: string;
  brandDisplayName: string;
  headOfficeAddress: string;
  industrySector: string;
  estimatedEmployeeCount: string;
  parentDomain: string;
  crossBorderProcessing: boolean;
  crossBorderJurisdictions: string;
  NIN: string;
  TIN: string;
  cacRegNumber?: string;

  // Section 3
  totalPhase1Users: string;
  roleBasedAccessRequired: boolean;
  roleBasedAccessDetails: string;
  thirdPartyAccessDeclared: boolean;
  thirdPartyAccessDetails: string;

  // Section 4
  billingContactName: string;
  billingContactEmail: string;
  billingContactPhone: string;
  preferredInvoicingEmail: string;
  paymentMethodPreference: string;
  specialInvoicingInstructions: string;
  accessLevel?: string;
  // Section 5
  personnel: {
    jobTitle: string;
    firstName: string;
    lastName: string;
    email: string;
    accessLevel: number;
  }[];

  // Section 6
  existingHRIS: string;
  hrisIntegrationRequired: boolean;
  hrisIntegrationDetails: string;
  jobPostingPlatforms: string;
  emailProvider: string;
  smsGatewayProvider: string;
  biometricIntegrationRequired: boolean;
  biometricDetails: string;

  // Section 7
  platformDisplayName: string;
  primaryBrandColor: string;
  customDomain: string;
  complianceRules: string;

  // Section 8
  additionalDeploymentNotes: string;

  // Section 9
  scopeConfirmed: boolean;
  expansionAcknowledged: boolean;
  unauthorizedBreachAcknowledged: boolean;
  dataComplianceConfirmed: boolean;
  electronicAgreement: boolean;

  // Sign Off
  authorizedFullName: string;
  authorizedTitle: string;
  authorizedDate: string;
}

interface OnboardingFormContentProps {
  formData: OnboardingFormData;
  onFormChange: (field: keyof OnboardingFormData, value: any) => void;
  onChildOrgChange: (
    index: number,
    field: keyof OnboardingFormData["childOrganizations"][0],
    value: string,
  ) => void;
  addChildOrg: () => void;
  removeChildOrg: (index: number) => void;
  onStakeholderChange: (
    index: number,
    field: keyof OnboardingFormData["personnel"][0],
    value: any,
  ) => void;
  addStakeholder: () => void;
  removeStakeholder: (index: number) => void;
  logoFile: File | null;
  logoPreview: string | null;
  onLogoUpload: (file: File) => void;
  onRemoveLogo: () => void;
  cacFile: File | null;
  tinFile: File | null;
  ninFile: File | null;
  onCacUpload: (file: File) => void;
  onTinUpload: (file: File) => void;
  onNinUpload: (file: File) => void;
  companyProfileFile: File | null;
  onCompanyProfileUpload: (file: File) => void;
}

const inputClass =
  "w-full px-3 py-2 flex items-center justify-between border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors";

const labelClass = "block text-sm font-medium text-gray-700 mb-1";

const checkboxRowClass = "flex items-start gap-3 py-1.5";

const sectionHeaderClass =
  "text-lg font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200";

const OnboardingFormContent: React.FC<OnboardingFormContentProps> = ({
  formData,
  onFormChange,
  onChildOrgChange,
  addChildOrg,
  removeChildOrg,
  onStakeholderChange,
  addStakeholder,
  removeStakeholder,
  logoFile,
  logoPreview,
  onLogoUpload,
  onRemoveLogo,
  cacFile,
  tinFile,
  ninFile,
  onCacUpload,
  onTinUpload,
  onNinUpload,
  companyProfileFile,
  onCompanyProfileUpload,
}) => {
  return (
    <div className="font-abel text-gray-700 text-sm leading-relaxed space-y-8 p-6 bg-white shadow-sm rounded-lg border border-gray-100 max-w-[1024px] mx-auto">
      {/* Header */}
      <div className="text-center mb-6 border-b pb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          25th Suite Onboarding and Pre Go Live Setup Checklist
        </h1>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-left max-w-lg mx-auto">
          <div>
            <label className={labelClass}>Client</label>
            <input
              type="text"
              value={formData.clientName}
              onChange={(e) => onFormChange("clientName", e.target.value)}
              placeholder="Customer Name"
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Date Submitted</label>
            <input
              type="date"
              value={formData.dateSubmitted}
              onChange={(e) => onFormChange("dateSubmitted", e.target.value)}
              className={inputClass}
            />
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Prepared by: Client Representative
        </p>
      </div>

      {/* Purpose */}
      <section>
        <h2 className={sectionHeaderClass}>Purpose</h2>
        <p className="text-gray-600 leading-relaxed">
          This checklist captures the information required to prepare the secure
          deployment and configuration of the 25th Suite, including 25th AI
          Recruiter, 25th Direct, 25th Console
          {formData.includeEmployeeLifecycle &&
            ", and the Employee Lifecycle Suite"}
          .
        </p>
        <div className={`${checkboxRowClass} mt-3`}>
          <input
            type="checkbox"
            checked={formData.includeEmployeeLifecycle}
            onChange={(e) =>
              onFormChange("includeEmployeeLifecycle", e.target.checked)
            }
            className="w-4 h-4 mt-0.5 accent-primary cursor-pointer"
          />
          <span className="text-sm text-gray-700">
            Include Employee Lifecycle Suite
          </span>
        </div>
        <p className="text-gray-600 leading-relaxed mt-3 text-xs bg-gray-50 p-3 rounded-md">
          Completion of this document is required before invoicing can be
          generated. All information provided is confidential and used solely
          for setup, configuration, and compliance with the Enterprise Services
          Agreement Package.
        </p>
      </section>

      {/* Section 1: Pilot Companies */}
      <section>
        <h2 className={sectionHeaderClass}>
          Section 1: Pilot Companies and Scope
        </h2>
        <div className="space-y-4">
          <div className={checkboxRowClass}>
            {/* <input
              type="checkbox"
              checked={formData.pilotCompaniesConfirmed}
              onChange={(e) =>
                onFormChange("pilotCompaniesConfirmed", e.target.checked)
              }
              className="w-4 h-4 mt-0.5 accent-primary cursor-pointer"
            /> */}
            <div className="flex-1">
              <span>Number of companies for initial pilot phase confirmed</span>
              <input
                type="text"
                value={formData.numberOfPilotCompanies}
                onChange={(e) =>
                  onFormChange("numberOfPilotCompanies", e.target.value)
                }
                placeholder="Number selected"
                className={`${inputClass} mt-2`}
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="font-medium text-gray-700">
                Company Details provided for pilot
              </p>
              <button
                onClick={addChildOrg}
                className="text-primary hover:text-primary/80 flex items-center gap-1 text-sm font-medium"
                type="button"
              >
                <span className="bg-primary/10 rounded-full w-5 h-5 flex items-center justify-center">
                  +
                </span>
                Add Company
              </button>
            </div>
            <div className="space-y-4">
              {formData.childOrganizations.map((org, i) => (
                <div
                  key={`org-${i}`}
                  className="bg-gray-50 p-4 rounded-lg border border-gray-200 relative"
                >
                  {formData.childOrganizations.length > 1 && (
                    <button
                      onClick={() => removeChildOrg(i)}
                      className="absolute -top-2 -right-2 bg-red-100 text-red-600 rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-200 transition-colors shadow-sm"
                      type="button"
                    >
                      -
                    </button>
                  )}
                  <h4 className="text-xs font-semibold text-gray-500 mb-1 uppercase">
                    {i === 0 ? "Parent Company" : `Sub Company ${i}`}
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">
                        Company Name
                      </label>
                      <input
                        type="text"
                        value={org.name}
                        onChange={(e) =>
                          onChildOrgChange(i, "name", e.target.value)
                        }
                        placeholder={`e.g. Business A`}
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">
                        Domain
                      </label>
                      <input
                        type="text"
                        value={org.domain}
                        onChange={(e) =>
                          onChildOrgChange(i, "domain", e.target.value)
                        }
                        placeholder={`e.g. business.com`}
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">
                        Contact Email
                      </label>
                      <input
                        type="email"
                        value={org.contactEmail}
                        onChange={(e) =>
                          onChildOrgChange(i, "contactEmail", e.target.value)
                        }
                        placeholder={`e.g. admin@business.com`}
                        className={inputClass}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* <div>
            <p className="font-medium text-gray-700 mb-2">
              Legal entity registration numbers provided where available
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {formData.legalEntityNumbers.map((num, i) => (
                <div key={`entity-${i}`}>
                  <label className="text-xs text-gray-500">
                    Company {i + 1}
                  </label>
                  <input
                    type="text"
                    value={num}
                    onChange={(e) => onLegalEntityChange(i, e.target.value)}
                    placeholder={`CAC Registration number`}
                    className={inputClass}
                  />
                </div>
              ))}
            </div>
          </div> */}
        </div>
      </section>

      {/* Section 2: Company Details */}
      <section>
        <h2 className={sectionHeaderClass}>
          Section 2: Company Details for Primary Company
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="col-span-1">
            <label className={labelClass}>Legal company name</label>
            <input
              type="text"
              value={formData.legalCompanyName}
              onChange={(e) => onFormChange("legalCompanyName", e.target.value)}
              placeholder="As per registration"
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Brand or short display name</label>
            <input
              type="text"
              value={formData.brandDisplayName}
              onChange={(e) => onFormChange("brandDisplayName", e.target.value)}
              placeholder="Display name"
              className={inputClass}
            />
          </div>
          <div className="md:col-span-1">
            <label className={labelClass}>Head office address</label>
            <input
              type="text"
              value={formData.headOfficeAddress}
              onChange={(e) =>
                onFormChange("headOfficeAddress", e.target.value)
              }
              placeholder="Full address"
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Industry or sector</label>
            <input
              type="text"
              value={formData.industrySector}
              onChange={(e) => onFormChange("industrySector", e.target.value)}
              placeholder="e.g. Technology, Finance"
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>
              Estimated total employee count across pilot companies
            </label>
            <input
              type="text"
              value={formData.estimatedEmployeeCount}
              onChange={(e) =>
                onFormChange("estimatedEmployeeCount", e.target.value)
              }
              placeholder="Total count"
              className={inputClass}
            />
          </div>
          <div className="md:col-span-1">
            <label className={labelClass}>Company Domain</label>
            <input
              type="text"
              // value={formData.estimatedEmployeeCount}
              value={formData.parentDomain}
              onChange={(e) => onFormChange("parentDomain", e.target.value)}
              placeholder="e.g. company.com"
              className={inputClass}
            />
          </div>

          <div className="md:col-span-1">
            <label className={labelClass}>CAC Registration Number</label>
            <input
              type="text"
              value={formData.cacRegNumber}
              onChange={(e) => onFormChange("cacRegNumber", e.target.value)}
              placeholder="RC Number"
              className={inputClass}
            />
          </div>
          <div className="md:col-span-1">
            <label className={labelClass}>
              TIN (Tax Identification Number)
            </label>
            <input
              type="text"
              value={formData.TIN}
              onChange={(e) => onFormChange("TIN", e.target.value)}
              placeholder="TIN Number"
              className={inputClass}
            />
          </div>
          <div className="md:col-span-1">
            <label className={labelClass}>
              CAC Document <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="file"
                onChange={(e) =>
                  e.target.files?.[0] && onCacUpload(e.target.files[0])
                }
                className="hidden"
                id="cac-upload"
                accept=".pdf,.jpg,.jpeg,.png"
              />
              <label
                htmlFor="cac-upload"
                className={`${inputClass} cursor-pointer hover:bg-gray-50 flex items-center justify-center border-dashed border-2`}
              >
                {cacFile ? cacFile.name : "Upload CAC Doc"}
              </label>
            </div>
          </div>
          <div className="md:col-span-1">
            <div className={checkboxRowClass}>
              <input
                type="checkbox"
                checked={formData.crossBorderProcessing}
                onChange={(e) =>
                  onFormChange("crossBorderProcessing", e.target.checked)
                }
                className="w-4 h-4 mt-0.5 accent-primary cursor-pointer"
              />
              <span>Cross border data processing requirement confirmed</span>
            </div>
            {formData.crossBorderProcessing && (
              <input
                type="text"
                value={formData.crossBorderJurisdictions}
                onChange={(e) =>
                  onFormChange("crossBorderJurisdictions", e.target.value)
                }
                placeholder="Jurisdictions listed"
                className={`${inputClass} mt-2`}
              />
            )}
          </div>
        </div>
      </section>

      {/* Section 3: Number of Users */}
      <section>
        <h2 className={sectionHeaderClass}>
          Section 3: Number of Users and Access
        </h2>
        <div className="space-y-4">
          <div>
            <label className={labelClass}>Total number of Phase 1 users</label>
            <input
              type="text"
              value={formData.totalPhase1Users}
              onChange={(e) => onFormChange("totalPhase1Users", e.target.value)}
              placeholder="Number of users"
              className={inputClass}
            />
          </div>
          <div>
            <div className={checkboxRowClass}>
              <input
                type="checkbox"
                checked={formData.roleBasedAccessRequired}
                onChange={(e) =>
                  onFormChange("roleBasedAccessRequired", e.target.checked)
                }
                className="w-4 h-4 mt-0.5 accent-primary cursor-pointer"
              />
              <span>Role based access control customization required</span>
            </div>
            {formData.roleBasedAccessRequired && (
              <textarea
                value={formData.roleBasedAccessDetails}
                onChange={(e) =>
                  onFormChange("roleBasedAccessDetails", e.target.value)
                }
                placeholder="Provide details"
                className={`${inputClass} mt-2`}
                rows={2}
              />
            )}
          </div>
          <div>
            <div className={checkboxRowClass}>
              <input
                type="checkbox"
                checked={formData.thirdPartyAccessDeclared}
                onChange={(e) =>
                  onFormChange("thirdPartyAccessDeclared", e.target.checked)
                }
                className="w-4 h-4 mt-0.5 accent-primary cursor-pointer"
              />
              <span>
                Third party consultants or vendors requiring limited access
                declared
              </span>
            </div>
            {formData.thirdPartyAccessDeclared && (
              <textarea
                value={formData.thirdPartyAccessDetails}
                onChange={(e) =>
                  onFormChange("thirdPartyAccessDetails", e.target.value)
                }
                placeholder="List third parties and purpose"
                className={`${inputClass} mt-2`}
                rows={2}
              />
            )}
          </div>
        </div>
      </section>

      {/* Section 4: Billing */}
      <section>
        <h2 className={sectionHeaderClass}>
          Section 4: Billing and Payment Contact
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>
              Billing contact name<span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.billingContactName}
              onChange={(e) =>
                onFormChange("billingContactName", e.target.value)
              }
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>
              Billing contact email<span className="text-red-500">*</span>
            </label>
            <div className="flex">
              <input
                type="text"
                value={formData.billingContactEmail.split("@")[0]}
                onChange={(e) => {
                  const prefix = e.target.value;
                  const domain = formData.parentDomain || "domain.com";
                  onFormChange("billingContactEmail", `${prefix}@${domain}`);
                }}
                placeholder="e.g. billing"
                className={`${inputClass} rounded-r-none border-r-0`}
              />
              <div className="bg-gray-100 px-3 flex items-center rounded-r-xl border border-gray-300 text-gray-500 text-sm whitespace-nowrap">
                @{formData.parentDomain || "domain.com"}
              </div>
            </div>
          </div>
          <div>
            <label className={labelClass}>
              Billing contact phone<span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              value={formData.billingContactPhone}
              onChange={(e) =>
                onFormChange("billingContactPhone", e.target.value)
              }
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Preferred invoicing email</label>
            <div className="flex">
              <input
                type="text"
                value={formData.preferredInvoicingEmail.split("@")[0]}
                onChange={(e) => {
                  const prefix = e.target.value;
                  const domain = formData.parentDomain || "domain.com";
                  onFormChange(
                    "preferredInvoicingEmail",
                    `${prefix}@${domain}`,
                  );
                }}
                placeholder="e.g. invoices"
                className={`${inputClass} rounded-r-none border-r-0`}
              />
              <div className="bg-gray-100 px-3 flex items-center rounded-r-xl border border-gray-300 text-gray-500 text-sm whitespace-nowrap">
                @{formData.parentDomain || "domain.com"}
              </div>
            </div>
          </div>
          <div>
            <label className={labelClass}>Payment method preference</label>
            <select
              value={formData.paymentMethodPreference}
              onChange={(e) =>
                onFormChange("paymentMethodPreference", e.target.value)
              }
              className={inputClass}
            >
              <option value="">Select payment method</option>
              <option value="bank_transfer">Bank Transfer</option>
              <option value="paypal">PayPal</option>
              <option value="paystack">Paystack</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className={labelClass}>Special invoicing instructions</label>
            <input
              type="text"
              value={formData.specialInvoicingInstructions}
              onChange={(e) =>
                onFormChange("specialInvoicingInstructions", e.target.value)
              }
              placeholder="If applicable"
              className={inputClass}
            />
          </div>
        </div>
      </section>

      {/* Section 5: Key Stakeholders */}
      <section>
        <h2 className={sectionHeaderClass}>
          <div className="flex items-center justify-between">
            <div className="group relative left-0 flex items-center gap-2">
              <div className="flex items-center gap-2">
                Section 5: Key Stakeholders and Decision
                <Info className="w-4 h-4 text-gray-400 cursor-help" />
              </div>
              <div className="invisible group-hover:visible absolute left-48 top-12 -translate-y-1/2 ml-2 z-50 bg-white shadow-xl border border-gray-100 rounded-lg p-3 w-max min-w-[200px]">
                <div className="grid grid-cols-1 gap-x-4 gap-y-2 text-sm">
                  <div className="text-gray-500 text-xs">
                    Access Level 0 is the highest level for business owner, 5 is
                    the lowest
                  </div>
                </div>
              </div>
            </div>
            <button
              onClick={addStakeholder}
              className="text-primary hover:text-primary/80 flex items-center gap-1 text-sm font-medium"
              type="button"
            >
              <span className="bg-primary/10 rounded-full w-5 h-5 flex items-center justify-center">
                +
              </span>
              Add Stakeholder
            </button>
          </div>
        </h2>
        <div className="space-y-6">
          {formData.personnel.map((person, i) => (
            <div
              key={`person-${i}`}
              className="bg-gray-50 p-5 rounded-xl border border-gray-200 relative group/stakeholder"
            >
              {formData.personnel.length > 1 && (
                <button
                  onClick={() => removeStakeholder(i)}
                  className="absolute -top-2 -right-2 bg-red-100 text-red-600 rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-200 transition-colors shadow-sm opacity-0 group-hover/stakeholder:opacity-100"
                  type="button"
                >
                  -
                </button>
              )}
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-start">
                <div className="md:col-span-1">
                  <label className={labelClass}>Job Title</label>
                  <select
                    value={person.jobTitle}
                    onChange={(e) =>
                      onStakeholderChange(i, "jobTitle", e.target.value)
                    }
                    className={inputClass}
                  >
                    <option value="">Select Job Title</option>
                    <option value="Business Owner">Business Owner</option>
                    <option value="Project Manager">Project Manager</option>
                    <option value="HR Lead">HR Lead</option>
                    <option value="IT Lead">IT Lead</option>
                    <option value="Finance Lead">Finance Lead</option>
                    <option value="Data Protection Officer">
                      Data Protection Officer
                    </option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>First Name</label>
                  <input
                    type="text"
                    value={person.firstName}
                    onChange={(e) =>
                      onStakeholderChange(i, "firstName", e.target.value)
                    }
                    placeholder="First Name"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Last Name</label>
                  <input
                    type="text"
                    value={person.lastName}
                    onChange={(e) =>
                      onStakeholderChange(i, "lastName", e.target.value)
                    }
                    placeholder="Last Name"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Email Address</label>
                  <div className="flex">
                    <input
                      type="text"
                      value={person.email.split("@")[0]}
                      onChange={(e) => {
                        const prefix = e.target.value;
                        const domain = formData.parentDomain || "domain.com";
                        onStakeholderChange(i, "email", `${prefix}@${domain}`);
                      }}
                      placeholder="e.g. john.doe"
                      className={`${inputClass} rounded-r-none border-r-0`}
                    />
                    <div className="bg-gray-100 px-3 flex items-center rounded-r-xl border border-gray-300 text-gray-500 text-sm whitespace-nowrap">
                      @{formData.parentDomain || "domain.com"}
                    </div>
                  </div>
                </div>
                <div>
                  <label className={labelClass}>Access Level</label>
                  <select
                    value={person.accessLevel}
                    onChange={(e) =>
                      onStakeholderChange(
                        i,
                        "accessLevel",
                        parseInt(e.target.value),
                      )
                    }
                    className={inputClass}
                  >
                    <option value={0}>0</option>
                    <option value={1}>1</option>
                    <option value={2}>2</option>
                    <option value={3}>3</option>
                    <option value={4}>4</option>
                    <option value={5}>5</option>
                  </select>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Section 6: Access and Integration */}
      <section>
        <h2 className={sectionHeaderClass}>
          Section 6: Access and Integration Information
        </h2>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Existing HRIS or ERP system</label>
              <input
                type="text"
                value={formData.existingHRIS}
                onChange={(e) => onFormChange("existingHRIS", e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>
                Job posting platforms currently used
              </label>
              <input
                type="text"
                value={formData.jobPostingPlatforms}
                onChange={(e) =>
                  onFormChange("jobPostingPlatforms", e.target.value)
                }
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>
                Email provider for system notifications
              </label>
              <input
                type="text"
                value={formData.emailProvider}
                onChange={(e) => onFormChange("emailProvider", e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>SMS gateway provider</label>
              <input
                type="text"
                value={formData.smsGatewayProvider}
                onChange={(e) =>
                  onFormChange("smsGatewayProvider", e.target.value)
                }
                placeholder="Where applicable"
                className={inputClass}
              />
            </div>
          </div>
          <div>
            <div className={checkboxRowClass}>
              <input
                type="checkbox"
                checked={formData.hrisIntegrationRequired}
                onChange={(e) =>
                  onFormChange("hrisIntegrationRequired", e.target.checked)
                }
                className="w-4 h-4 mt-0.5 accent-primary cursor-pointer"
              />
              <span>HRIS or ERP integration requirement confirmed</span>
            </div>
            {formData.hrisIntegrationRequired && (
              <textarea
                value={formData.hrisIntegrationDetails}
                onChange={(e) =>
                  onFormChange("hrisIntegrationDetails", e.target.value)
                }
                placeholder="Technical or API information"
                className={`${inputClass} mt-2`}
                rows={2}
              />
            )}
          </div>
          <div>
            <div className={checkboxRowClass}>
              <input
                type="checkbox"
                checked={formData.biometricIntegrationRequired}
                onChange={(e) =>
                  onFormChange("biometricIntegrationRequired", e.target.checked)
                }
                className="w-4 h-4 mt-0.5 accent-primary cursor-pointer"
              />
              <span>
                Biometric attendance device integration requirement confirmed
              </span>
            </div>
            {formData.biometricIntegrationRequired && (
              <textarea
                value={formData.biometricDetails}
                onChange={(e) =>
                  onFormChange("biometricDetails", e.target.value)
                }
                placeholder="Device model or API details"
                className={`${inputClass} mt-2`}
                rows={2}
              />
            )}
          </div>
        </div>
      </section>

      {/* Section 7: Customization and Branding */}
      <section>
        <h2 className={sectionHeaderClass}>
          Section 7: Customization and Branding
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>
              Preferred platform display name
            </label>
            <input
              type="text"
              value={formData.platformDisplayName}
              onChange={(e) =>
                onFormChange("platformDisplayName", e.target.value)
              }
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Company logo (high resolution)</label>
            {!logoPreview ? (
              <input
                type="file"
                accept="image/jpeg,image/jpg,image/png"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) onLogoUpload(file);
                }}
                className="block w-full text-sm text-gray-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-md file:border-0
                  file:text-sm file:font-semibold
                  file:bg-primary file:text-white
                  hover:file:bg-primary/90
                  cursor-pointer"
              />
            ) : (
              <div className="relative group cursor-pointer inline-block">
                <img
                  src={logoPreview}
                  alt="Company logo"
                  className="h-12 object-contain rounded border border-gray-200 p-1"
                />
                <button
                  onClick={onRemoveLogo}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 text-xs flex items-center justify-center
                    opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  ✕
                </button>
              </div>
            )}
          </div>
          <div>
            <label className={labelClass}>Primary brand color</label>
            <div className="flex gap-2 items-center">
              <input
                type="color"
                value={formData.primaryBrandColor || "#000000"}
                onChange={(e) =>
                  onFormChange("primaryBrandColor", e.target.value)
                }
                className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
              />
              <input
                type="text"
                value={formData.primaryBrandColor}
                onChange={(e) =>
                  onFormChange("primaryBrandColor", e.target.value)
                }
                placeholder="#000000"
                className={inputClass}
              />
            </div>
          </div>
          <div>
            <label className={labelClass}>
              Custom domain or subdomain (white label)
            </label>
            <input
              type="text"
              value={formData.customDomain}
              onChange={(e) => onFormChange("customDomain", e.target.value)}
              placeholder="e.g. hr.yourcompany.com"
              className={inputClass}
            />
          </div>
          <div className="md:col-span-2">
            <label className={labelClass}>
              Compliance rules or country specific configurations
            </label>
            <textarea
              value={formData.complianceRules}
              onChange={(e) => onFormChange("complianceRules", e.target.value)}
              className={inputClass}
              rows={2}
            />
          </div>
        </div>
      </section>

      {/* Section 8: Final Notes */}
      <section>
        <h2 className={sectionHeaderClass}>
          Section 8: Deployment Requirements
        </h2>
        <textarea
          value={formData.additionalDeploymentNotes}
          onChange={(e) =>
            onFormChange("additionalDeploymentNotes", e.target.value)
          }
          placeholder="Preferred go live date, compliance needs, training preferences, etc."
          className={`${inputClass} mt-2`}
          rows={3}
        />
        <div className="mt-4 max-w-[250px]">
          <label className={labelClass}>Company Profile / Brochure</label>
          <div className="relative">
            <input
              type="file"
              onChange={(e) =>
                e.target.files?.[0] && onCompanyProfileUpload(e.target.files[0])
              }
              className="hidden"
              id="company-profile-upload"
              accept=".pdf,.jpg,.jpeg,.png"
            />
            <label
              htmlFor="company-profile-upload"
              className={`${inputClass} cursor-pointer hover:bg-gray-50 flex items-center justify-center border-dashed border-2`}
            >
              {companyProfileFile
                ? companyProfileFile.name
                : "Upload Company Profile"}
            </label>
          </div>
        </div>
        <div className="mt-6 pt-6 border-t border-gray-200"></div>
      </section>

      {/* Section 9: Acknowledgment */}
      <section>
        <h2 className={sectionHeaderClass}>
          Section 9: Onboarding and Scope Acknowledgment
        </h2>
        <div className="space-y-3">
          <div className={checkboxRowClass}>
            <input
              type="checkbox"
              checked={formData.scopeConfirmed}
              onChange={(e) => onFormChange("scopeConfirmed", e.target.checked)}
              className="w-4 h-4 mt-0.5 accent-primary cursor-pointer"
            />
            <span className="text-sm leading-relaxed">
              Confirmation that onboarding and system access is limited strictly
              to the designated companies listed in this document and agreed in
              the MSA
            </span>
          </div>
          <div className={checkboxRowClass}>
            <input
              type="checkbox"
              checked={formData.expansionAcknowledged}
              onChange={(e) =>
                onFormChange("expansionAcknowledged", e.target.checked)
              }
              className="w-4 h-4 mt-0.5 accent-primary cursor-pointer"
            />
            <span className="text-sm leading-relaxed">
              Acknowledgment that additional subsidiaries, affiliates, or
              related entities require a formal expansion amendment and
              additional fees
            </span>
          </div>
          <div className={checkboxRowClass}>
            <input
              type="checkbox"
              checked={formData.unauthorizedBreachAcknowledged}
              onChange={(e) =>
                onFormChange("unauthorizedBreachAcknowledged", e.target.checked)
              }
              className="w-4 h-4 mt-0.5 accent-primary cursor-pointer"
            />
            <span className="text-sm leading-relaxed">
              Acknowledgment that unauthorized onboarding or access constitutes
              a material breach and may result in suspension, termination, and
              retroactive fees
            </span>
          </div>
          <div className={checkboxRowClass}>
            <input
              type="checkbox"
              checked={formData.dataComplianceConfirmed}
              onChange={(e) =>
                onFormChange("dataComplianceConfirmed", e.target.checked)
              }
              className="w-4 h-4 mt-0.5 accent-primary cursor-pointer"
            />
            <span className="text-sm leading-relaxed">
              Confirmation that all Personal Data shared complies with
              applicable Data Protection Laws including NDPA 2023 and has a
              lawful basis for processing
            </span>
          </div>
          <div className={checkboxRowClass}>
            <input
              type="checkbox"
              checked={formData.electronicAgreement}
              onChange={(e) =>
                onFormChange("electronicAgreement", e.target.checked)
              }
              className="w-4 h-4 mt-0.5 accent-primary cursor-pointer"
            />
            <span className="text-sm leading-relaxed">
              Electronic agreement to all onboarding, scope, and compliance
              terms
            </span>
          </div>
        </div>
      </section>

      {/* Authorized Sign Off */}
      <section className="border-t pt-6">
        <h2 className={sectionHeaderClass}>Authorized Sign Off</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className={labelClass}>Full Name</label>
            <input
              type="text"
              value={formData.authorizedFullName}
              onChange={(e) =>
                onFormChange("authorizedFullName", e.target.value)
              }
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Title (Role)</label>
            <input
              type="text"
              value={formData.authorizedTitle}
              onChange={(e) => onFormChange("authorizedTitle", e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Date</label>
            <input
              type="date"
              value={formData.authorizedDate}
              onChange={(e) => onFormChange("authorizedDate", e.target.value)}
              className={inputClass}
            />
          </div>
        </div>
      </section>

      {/* Next Steps */}
      <section className="bg-gray-50 p-4 rounded-lg border border-gray-200">
        <h2 className="text-lg font-bold text-gray-900 mb-3">Next Steps</h2>
        <ul className="space-y-2 text-sm text-gray-600">
          <li className="flex items-center gap-2">
            <span className="text-primary">☐</span> Form review by 25th Staffing
            within 24 hours
          </li>
          <li className="flex items-center gap-2">
            <span className="text-primary">☐</span> Invoice issued within 24 to
            48 hours
          </li>
          <li className="flex items-center gap-2">
            <span className="text-primary">☐</span> Kick off call scheduled
            after invoice payment
          </li>
        </ul>
      </section>
    </div>
  );
};

export type { OnboardingFormData };
export default OnboardingFormContent;
